// Déplacement d'un joueur : la même fonction tourne sur le client (prédiction) et sur le serveur (autorité).
import { PLAYER_RADIUS, SPEED } from './constants';
import { groundAt, moveCircle } from './geometry';
import type { GameMap } from './map';

export interface MoveInput {
  seq: number;
  dt: number; // s, borné par le serveur
  mx: number; // direction voulue dans le monde (normalisée ou nulle)
  my: number;
  sprint: boolean;
  crouch: boolean;
  aim: boolean;
  angle: number; // visée, radians
  pitch?: number; // visée en hauteur (radians, + vers le haut), pour l'allure des autres joueurs
  limp?: boolean; // jambe cassée : plus lent, pas de sprint
  jump?: boolean; // appui sur la touche de saut (une seule entrée)
}

// z : hauteur des pieds (0 au sol, plus haut en saut ou debout sur un objet) ; vz : vitesse verticale
export interface MoveState {
  x: number;
  y: number;
  z?: number;
  vz?: number;
  jcd?: number; // temps (s) avant de pouvoir ressauter : la réception se joue, pas de sauts en rafale
}

// Saut : environ 0,6 m de haut, 0,65 s en l'air ; on monte sans sauter sur ce qui fait moins de STEP
export const JUMP = { v: 3.6, g: 11, step: 0.3, cooldown: 0.45 };
export const airborne = (s: MoveState) => (s.vz ?? 0) !== 0;

export function moveSpeed(i: MoveInput): number {
  if (i.limp) return Math.min(i.crouch ? SPEED.crouch : SPEED.walk * 0.7, SPEED.walk * 0.7);
  if (i.crouch) return SPEED.crouch;
  if (i.aim) return SPEED.aim;
  return i.sprint ? SPEED.sprint : SPEED.walk;
}

export function isSprinting(i: MoveInput): boolean {
  return i.sprint && !i.limp && !i.crouch && !i.aim && (i.mx !== 0 || i.my !== 0);
}

export function stepMove(map: GameMap, s: MoveState, i: MoveInput): void {
  let z = s.z ?? 0, vz = s.vz ?? 0, jcd = Math.max(0, (s.jcd ?? 0) - i.dt);
  const ground0 = groundAt(map, s.x, s.y, PLAYER_RADIUS, z + JUMP.step);
  // Saut : seulement les pieds posés, un peu après la dernière réception, pas accroupi ni avec une jambe cassée
  if (i.jump && vz === 0 && jcd <= 0 && z <= ground0 + 0.01 && !i.crouch && !i.limp) vz = JUMP.v;
  // Déplacement : en l'air ou sur un objet, le couvert bas sous les pieds (plus une marche) ne bloque pas
  const l = Math.hypot(i.mx, i.my);
  if (l > 1e-6) {
    const v = moveSpeed(i) * i.dt;
    const lift = z > 0.01 || vz !== 0 ? z + JUMP.step : -1;
    [s.x, s.y] = moveCircle(map, s.x, s.y, (i.mx / l) * v, (i.my / l) * v, PLAYER_RADIUS, lift);
  }
  // Pesanteur : on retombe sur le sol, ou sur l'objet sous les pieds
  const ground = groundAt(map, s.x, s.y, PLAYER_RADIUS, z + JUMP.step);
  if (vz !== 0 || z > ground + 1e-3) {
    z += vz * i.dt - 0.5 * JUMP.g * i.dt * i.dt;
    vz -= JUMP.g * i.dt;
    if (z <= ground) { z = ground; vz = 0; jcd = JUMP.cooldown; }
  } else z = ground; // marche montée (moins de 30 cm)
  s.z = Math.round(z * 1000) / 1000; s.vz = Math.round(vz * 1000) / 1000; s.jcd = Math.round(jcd * 1000) / 1000;
}
